import { useState, useEffect, use, createContext } from 'react';
import { Amplify } from 'aws-amplify';
import * as Auth from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';
import { queryClient } from '@/utils/query-client';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN,
          scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code',
          providers: ['Google'],
        }
      }
    },
  },
});

function useCognitoAuth() {
  const [user, setUser] = useState<Auth.AuthUser|null>(null);
  const isSignedIn = Boolean(user);

  const [inProgress, setInProgress] = useState(true);

  const signIn = async (email: string) => {
    const output = await Auth.signIn({
      username: email,
      options: {
        authFlowType: 'USER_AUTH',
        preferredChallenge: 'EMAIL_OTP',
      },
    });

    return output;
  };

  const confirmSignIn = async (code: string) => {
    const output = await Auth.confirmSignIn({ challengeResponse: code });

    if (output.isSignedIn) {
      fetchCurrentAuthUser();
    }

    return output;
  };

  const signInWithRedirect = async (provider: 'Google') =>
    Auth.signInWithRedirect({ provider });

  const signOut = async () => {
    await Auth.signOut();
    queryClient.clear();
    Sentry.setUser(null);
    setUser(null);
    window.location.replace('/login');
  };

  const fetchCurrentAuthUser = async () => {
    try {
      const user = await Auth.getCurrentUser();
      setUser(user);
    } catch {
      setUser(null);
    } finally {
      setInProgress(false);
    }
  };

  const setSentryEmail = async () => {
    if (!user) return;

    try {
      const tokens = await Auth.fetchAuthSession();
      const email = tokens.tokens?.idToken?.payload?.email;

      if (!email) return;

      Sentry.setUser({ email: email as string });
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'NotAuthorizedException' || e.name === 'UserNotFoundException') {
          return signOut();
        }
      }
    }
  };

  useEffect(() => {
    setSentryEmail();
  }, [user]);

  useEffect(() => {
    fetchCurrentAuthUser();
  }, []);

  return {
    isSignedIn,
    inProgress,
    signIn,
    confirmSignIn,
    signInWithRedirect,
    signOut,
  };
}

export interface IAuthContext {
  isSignedIn: boolean;
  inProgress: boolean;
  signIn: (email: string) => Promise<Auth.SignInOutput>;
  confirmSignIn: (code: string) => Promise<Auth.ConfirmSignInOutput>;
  signInWithRedirect: (provider: 'Google') => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<IAuthContext>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useCognitoAuth();
  return <AuthContext value={auth}>{children}</AuthContext>;
}

export const useAuth = () => {
  return use(AuthContext);
};
